import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { BrainCircuit, Layers, History, Bookmark, X, Zap } from "lucide-react";
import { CandidateProfile } from "../../../Candidate/Types/CandidateType";
import {
  TalentFilters,
  TalentIntelligenceDashboard,
  TalentMetric,
  TalentSearchResponse,
  buildChips,
  decodeRawFilter,
  initialTalentFilters,
  serializeTalentFilters,
  talentFiltersActiveCount,
  DEFAULT_RESULT_COLUMNS,
} from "../Types/TalentIntelligenceTypes";
import {
  getTalentIntelligenceDashboard,
  searchTalentIntelligence,
} from "../Services/TalentIntelligenceService";
import {
  PersistedIntelligenceSearch,
  loadSavedSearches,
  loadSearchHistory,
  recordSearch,
  deleteSearchHistoryEntry,
  clearSearchHistory,
  saveSavedSearch,
  deleteSavedSearch,
} from "../Services/IntelligencePersistence";
import { GlobalSearchBar } from "../Components/GlobalSearchBar";
import { AdvancedFilterPanel } from "../Components/AdvancedFilterPanel";
import { FilterChipView } from "../Components/FilterChip";
import { TalentIntelligenceDashboardView } from "../Components/TalentIntelligenceDashboard";
import { CandidateResultTable } from "../Components/CandidateResultTable";
import { SavedSearchesDrawer } from "../Components/SavedSearchesDrawer";
import { SearchHistoryDrawer } from "../Components/SearchHistoryDrawer";
import { FilterChip } from "../Types/TalentIntelligenceTypes";
import CandidateProfileView from "../../../Candidate/Components/CandidateProfileView";

const EMPTY_RESPONSE: TalentSearchResponse = {
  content: [],
  page: 0,
  size: 20,
  totalElements: 0,
  totalPages: 0,
  first: true,
  last: true,
  hasNext: false,
  hasPrevious: false,
};

export const TalentIntelligencePage: React.FC = () => {
  const [filters, setFilters] = useState<TalentFilters>(initialTalentFilters);
  const [results, setResults] = useState<TalentSearchResponse>(EMPTY_RESPONSE);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<TalentIntelligenceDashboard | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columns, setColumns] = useState<string[]>(DEFAULT_RESULT_COLUMNS);
  const [savedSearches, setSavedSearches] = useState<PersistedIntelligenceSearch[]>([]);
  const [history, setHistory] = useState<PersistedIntelligenceSearch[]>([]);
  const [drawer, setDrawer] = useState<null | "saved" | "history">(null);
  const [detailCandidate, setDetailCandidate] = useState<CandidateProfile | null>(null);
  const [flashResults, setFlashResults] = useState(false);
  const [appliedSource, setAppliedSource] = useState<{ label: string; count: number } | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const flashTimerRef = useRef<number | null>(null);

  const revealResults = useCallback(() => {
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlashResults(true);
    if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashResults(false), 1600);
  }, []);

  useEffect(() => {
    setSavedSearches(loadSavedSearches());
    setHistory(loadSearchHistory());
  }, []);

  const runSearch = useCallback(
    async (filtersToUse: TalentFilters, pageNum: number, record = false) => {
      setLoading(true);
      try {
        const response = await searchTalentIntelligence(
          serializeTalentFilters(filtersToUse),
          pageNum,
          20
        );
        if (response.success && response.data) {
          setResults(response.data);
          setPage(response.data.page);
        } else {
          setResults({ ...EMPTY_RESPONSE });
        }
      } catch (err) {
        console.error("Talent intelligence search failed", err);
        setResults({ ...EMPTY_RESPONSE });
      } finally {
        setLoading(false);
      }

      if (record) {
        const hasQuery = Boolean(filtersToUse.keyword?.trim());
        const isMeaningful = hasQuery || talentFiltersActiveCount(filtersToUse) > 0;
        if (isMeaningful) {
          setHistory(recordSearch({ term: filtersToUse.keyword, filters: filtersToUse }));
        }
      }
    },
    []
  );

  const fetchDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const response = await getTalentIntelligenceDashboard();
      if (response.success && response.data) setDashboard(response.data);
    } catch (err) {
      console.error("Talent intelligence dashboard failed", err);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    runSearch(initialTalentFilters, 0);
  }, [fetchDashboard, runSearch]);

  const applyPatch = useCallback(
    (patch: Partial<TalentFilters>) => {
      setAppliedSource(null);
      setFilters((prev) => {
        const next = { ...prev, ...patch };
        runSearch(next, 0);
        return next;
      });
    },
    [runSearch]
  );

  const applyMetric = useCallback(
    (metric: TalentMetric) => {
      const raw = metric?.filters || {};
      if (Object.keys(raw).length === 0) {
        // "All talent" style metric → reset to unrestricted pool
        setAppliedSource({ label: metric?.label || "Total Talent", count: metric?.count ?? 0 });
        setFilters((prev) => {
          const next = { ...initialTalentFilters };
          runSearch(next, 0, true);
          return next;
        });
        revealResults();
        return;
      }
      setAppliedSource({ label: metric?.label || "Segment", count: metric?.count ?? 0 });
      const next = { ...initialTalentFilters, ...decodeRawFilter(raw) };
      setFilters(next);
      runSearch(next, 0, true);
      revealResults();
    },
    [runSearch, revealResults]
  );

  const handleKeywordSearch = useCallback(
    (term: string) => {
      setAppliedSource(null);
      const next = { ...initialTalentFilters, keyword: term };
      setFilters(next);
      runSearch(next, 0, true);
    },
    [runSearch]
  );

  const applyStoredSearch = useCallback(
    (entry: PersistedIntelligenceSearch) => {
      setDrawer(null);
      setFilters(entry.filters);
      runSearch({ ...entry.filters }, 0, true);
      revealResults();
    },
    [runSearch, revealResults]
  );

  const handleSaveCurrentSearch = useCallback(
    (name: string) => {
      const next = saveSavedSearch(name, { term: filters.keyword, filters });
      setSavedSearches(next);
    },
    [filters]
  );

  const handleDeleteSavedSearch = useCallback((id: string) => {
    setSavedSearches(deleteSavedSearch(id));
  }, []);

  const handleDeleteHistoryEntry = useCallback((id: string) => {
    setHistory(deleteSearchHistoryEntry(id));
  }, []);

  const handleClearHistory = useCallback(() => {
    setHistory(clearSearchHistory());
  }, []);

  const removeChip = useCallback(
    (chip: FilterChip) => {
      const patch = chip.remove();
      applyPatch(patch);
    },
    [applyPatch]
  );

  const clearAllFilters = useCallback(() => {
    setAppliedSource(null);
    setFilters((prev) => {
      const next = { ...initialTalentFilters };
      runSearch(next, 0, true);
      return next;
    });
  }, [runSearch]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      runSearch(filters, nextPage);
    },
    [filters, runSearch]
  );

  const chips = useMemo(() => buildChips(filters), [filters]);
  const activeCount = useMemo(() => talentFiltersActiveCount(filters), [filters]);
  const canSaveCurrent = activeCount > 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 md:px-8 py-6">
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        {/* ── Header ── */}
        <header>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <BrainCircuit size={24} />
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
                Nexgent Recruiter Intelligence
              </h1>
              <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">
                Discover, filter, analyze and act on experienced professionals across
                leadership, career, business-impact, availability and engagement signals.
              </p>
            </div>
          </div>
        </header>

        {/* ── Global Search ── */}
        <GlobalSearchBar
          initialKeyword={filters.keyword}
          onSearch={handleKeywordSearch}
          onSelectHistory={applyStoredSearch}
          history={history}
          exploring={loading}
        />

        {/* ── Action bar ── */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:border-indigo-300 hover:text-indigo-700 flex items-center gap-2 shadow-sm"
          >
            <Layers size={15} className="text-indigo-500" />
            Advanced Filters
            {activeCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-black">
                {activeCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setDrawer("saved")}
            title="Open saved searches"
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:border-fuchsia-300 hover:text-fuchsia-700 flex items-center gap-2 shadow-sm"
          >
            <Bookmark size={15} className="text-fuchsia-500" /> Saved Searches
            {savedSearches.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-fuchsia-600 text-white text-[10px] font-black">
                {savedSearches.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setDrawer("history")}
            title="Open search history"
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:border-indigo-300 hover:text-indigo-700 flex items-center gap-2 shadow-sm"
          >
            <History size={15} className="text-indigo-500" /> Search History
            {history.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-800 text-white text-[10px] font-black">
                {history.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Today's Talent Intelligence ── */}
        <TalentIntelligenceDashboardView
          dashboard={dashboard}
          loading={dashboardLoading}
          onMetricClick={applyMetric}
          onRefresh={fetchDashboard}
        />

        {/* ── Active query builder (chips) ── */}
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">
              Active Search
            </h3>
            {chips.length > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-[11px] font-bold text-rose-600 hover:text-rose-800 flex items-center gap-1"
              >
                <X size={12} /> Clear all
              </button>
            )}
          </div>
          {chips.length === 0 ? (
            <p className="text-xs text-slate-400">
              No filters applied — showing the full searchable talent pool. Click any
              dashboard segment or open Advanced Filters to build a search.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {chips.map((chip) => (
                <FilterChipView key={chip.id} chip={chip} onRemove={removeChip} />
              ))}
            </div>
          )}
          {chips.length > 0 && (
            <p className="mt-2 text-[10px] text-slate-400 font-medium">
              Filters are combined with <span className="font-bold text-slate-500">AND</span> logic.
            </p>
          )}
        </div>

        {/* ── Search results ── */}
        {appliedSource && (
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/80 px-4 py-3 flex flex-wrap items-center gap-2 text-sm shadow-sm">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center shrink-0">
              <Zap size={14} />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-black text-slate-800">
                "{appliedSource.label}" segment
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-black">
                  {appliedSource.count.toLocaleString()} candidate{appliedSource.count === 1 ? "" : "s"}
                </span>
              </p>
              <p className="text-[10px] text-slate-500 font-medium">
                Showing exactly the candidates counted for this dashboard segment. The filters below are applied to the search.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="ml-auto px-3 py-1.5 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-[11px] font-bold hover:bg-indigo-600 hover:text-white transition-colors shrink-0"
            >
              View filters
            </button>
          </div>
        )}
        <div
          ref={resultsRef}
          id="intel-results"
          className={`scroll-mt-6 rounded-2xl transition-all duration-300 ${
            flashResults ? "ring-2 ring-indigo-300 shadow-lg shadow-indigo-100" : ""
          }`}
        >
        <CandidateResultTable
          results={results.content}
          totalElements={results.totalElements}
          page={results.page}
          totalPages={results.totalPages}
          loading={loading}
          onPageChange={handlePageChange}
          columns={columns}
          onColumnsChange={setColumns}
          onOpenDetails={setDetailCandidate}
        />
        </div>
      </div>

      {/* ── Advanced filter drawer ── */}
      <AdvancedFilterPanel
        open={filtersOpen}
        filters={filters}
        onChange={applyPatch}
        onClose={() => setFiltersOpen(false)}
        onApply={() => {
          setAppliedSource(null);
          setFiltersOpen(false);
          runSearch(filters, 0, true);
          revealResults();
        }}
      />

      {/* ── Saved searches drawer ── */}
      <SavedSearchesDrawer
        open={drawer === "saved"}
        onClose={() => setDrawer(null)}
        savedSearches={savedSearches}
        canSaveCurrent={canSaveCurrent}
        currentTerm={filters.keyword}
        currentFilterCount={activeCount}
        onApply={applyStoredSearch}
        onDelete={handleDeleteSavedSearch}
        onSave={handleSaveCurrentSearch}
      />

      {/* ── Search history drawer ── */}
      <SearchHistoryDrawer
        open={drawer === "history"}
        onClose={() => setDrawer(null)}
        history={history}
        onApply={applyStoredSearch}
        onDelete={handleDeleteHistoryEntry}
        onClear={handleClearHistory}
      />

      {/* ── Candidate detail modal ── */}
      <Transition show={detailCandidate !== null} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDetailCandidate(null)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform transition-all relative">
                  <button
                    onClick={() => setDetailCandidate(null)}
                    className="absolute -top-2 -right-2 z-20 p-2 bg-slate-900 rounded-full shadow-2xl border border-slate-800 text-slate-400 hover:text-cyan-400 transition-colors"
                    aria-label="Close profile"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {detailCandidate && (
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 overflow-hidden shadow-2xl">
                      <CandidateProfileView data={detailCandidate} />
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default TalentIntelligencePage;